import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import CircularProgress from '@mui/material/CircularProgress';
import { RevenueTrend, RevenueTrendsCached } from 'common/models/Reporting/Revenue';
import { RevenueFilter } from 'common/models/Reporting/AnalyticsFilterData';
import { GetRevenueTrendDataForGraph } from 'services/ReportingService';
import { GetDashboardSettings } from 'services/ConfigurationService';
import { NameIdObj } from 'common/models/GenericTypes';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';

interface Props {
  id: string;
  title: string;
  sourceType: string;
  userFilterIds: string;
  showConfigButton?: boolean;
  kioskToken?: string | null;
  refreshFlag?: boolean | null;
  saveConfigHandler?: (
    id: string,
    title: string,
    userFilterIds: string,
    sourceType: string
  ) => void;
}

const customTooltipCircleStyle: React.CSSProperties = {
  width: '10px',
  height: '10px',
  marginRight: '10px',
  borderRadius: '50%',
  alignSelf: 'center',
};

const getBarName = (
  dataKey: string | undefined,
  stretchOverride?: string,
  targetOverride?: string
) => {
  switch (dataKey) {
    case 'total':
      return 'Revenue';
    case 'target':
      return targetOverride ? targetOverride : 'Target';
    case 'stretch':
      return stretchOverride ? stretchOverride : 'Stretch Target';
    default:
      return '';
  }
};

const formatMonthText = (date: string) => {
  const [fullMonth, year] = date.split(' ');
  const shortMonth = fullMonth.slice(0, 3);
  return `${shortMonth} ${year}`;
};
export default function BarGraphRevenueTrend({
  id,
  title: titleProps,
  sourceType: sourceTypeProps,
  userFilterIds: userIdsProps,
  showConfigButton = false,
  kioskToken = null,
  refreshFlag = null,
  saveConfigHandler,
}: Props) {
  const t = useTheme();
  const [title, setTitle] = useState<string>('Historical Revenue');
  const [sourceType, setSourceType] = useState('');
  const [stretchLabel, setStretchLabel] = useState<string>();
  const [targetLabel, setTargetLabel] = useState<string>();
  const [data, setData] = useState<RevenueTrend[]>([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [cacheLastUpdated, setCacheLastUpdated] = useState('');
  const [errorHandler, setErrorHandler] = useState('');

  useEffect(() => {
    setTitle(titleProps);
    setSourceType(sourceTypeProps);
    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps.split(',').map(Number));
      }
    }
  }, [sourceTypeProps, userIdsProps, titleProps]);

  const cacheUpdateMessage = (date: Date) => {
    const now = moment();
    const duration = moment.duration(now.diff(date));

    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes()) % 60;

    if (hours === 0) {
      if (minutes === 0) {
        return 'a few seconds ago';
      } else if (minutes === 1) {
        return '1 minute ago';
      } else {
        return `${minutes} minutes ago`;
      }
    }

    if (hours === 1 && minutes <= 30) {
      return `${minutes} minutes ago`;
    }

    return `${hours} hours and ${minutes} minutes ago`;
  };

  const getRevenueDataFromServer = async (
    sourceType: string,
    userIds: string,
    kioskToken: string | null = null
  ) => {
    setIsLoading(true);
    let filterTypeId = 0;
    if (sourceType == 'consultants') {
      filterTypeId = 4;
    } else if (sourceType == 'groups') {
      filterTypeId = 3;
    } else if (sourceType == 'currentuser') {
      filterTypeId = 5;
    } else if (sourceType == 'company') {
      filterTypeId = 1;
    }

    if (filterTypeId > 0) {
      const filter = { userFilterID: userIds, userFilterTypeID: filterTypeId } as RevenueFilter;
      const res = await GetRevenueTrendDataForGraph(filter, kioskToken, setErrorHandler);
      if (res) {
        res.forEach((r) => {
          r.total = Math.round(r.total);
        });

        setData(res);

        const dashboardSettings = await GetDashboardSettings(kioskToken, setErrorHandler);
        if (dashboardSettings) {
          const s = dashboardSettings.find((v) => v.type === 'StretchLabelOverride');
          const t = dashboardSettings.find((v) => v.type === 'TargetLabelOverride');
          if (s) setStretchLabel(s.value);
          if (t) setTargetLabel(t.value);

          const date = new Date();
          const cacheValue = {
            data: res,
            timeCached: date,
            stretchLabel: s?.value,
            targetLabel: t?.value,
          } as RevenueTrendsCached;

          setCacheLastUpdated(cacheUpdateMessage(date));
          localStorage.setItem(id, JSON.stringify(cacheValue));
        }
      }
    }

    setIsLoading(false);
  };

  const cacheExpired = (cachedDate: Date): boolean => {
    const currentDate = new Date();
    const objectDate = new Date(cachedDate);

    const differenceInMilliseconds = currentDate.getTime() - objectDate.getTime();

    const fourHoursInMilliseconds = 4 * 60 * 60 * 1000;

    return differenceInMilliseconds >= fourHoursInMilliseconds;
  };

  const getRevenueData = async () => {
    const cachedValue = localStorage.getItem(id);
    if (cachedValue) {
      const cachedData = JSON.parse(cachedValue) as RevenueTrendsCached;
      if (cacheExpired(cachedData.timeCached)) {
        await getRevenueDataFromServer(sourceType, userIdsProps, kioskToken);
      } else {
        setData(cachedData.data);
        if (cachedData.stretchLabel) setStretchLabel(cachedData.stretchLabel);
        if (cachedData.targetLabel) setTargetLabel(cachedData.targetLabel);
        setCacheLastUpdated(cacheUpdateMessage(cachedData.timeCached));
      }
    } else {
      await getRevenueDataFromServer(sourceType, userIdsProps, kioskToken);
    }
  };

  useEffect(() => {
    !showConfigDialog && getRevenueData();
  }, [sourceType, showConfigDialog, kioskToken]);

  useEffect(() => {
    if (refreshFlag && kioskToken) {
      getRevenueData();
    }
  }, [sourceType, refreshFlag, kioskToken]);

  const mapIdsToString = useCallback(() => {
    let selectedIds = '';
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers.join(',');
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups.join(',');
    }
    return selectedIds;
  }, [selectedUsers, selectedGroups, sourceType]);

  const onSaveConfig = useCallback(async () => {
    console.log(mapIdsToString());
    saveConfigHandler && saveConfigHandler(id, title, mapIdsToString(), sourceType);
    setShowConfigDialog(false);
    await getRevenueDataFromServer(sourceType, mapIdsToString(), kioskToken);
  }, [saveConfigHandler, selectedUsers, selectedGroups, sourceType, title, id, kioskToken]);

  const sourceTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSourceType(value);
  }, []);

  const onNumericValuesChange = useCallback((values: NameIdObj[], type: string) => {
    const ids = values.map((v) => v.id);
    if (type == 'consultants') {
      setSelectedUsers(ids);
    }

    if (type == 'groups') {
      setSelectedGroups(ids);
    }
  }, []);

  const CustomTooltip = useCallback(
    ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        return (
          <Box
            bgcolor={(t) => t.palette.background.paper}
            border={(t) => `solid 1px ${t.palette.text.disabled}`}
            p={1}
          >
            <div style={{ textAlign: 'center', fontWeight: 600 }}>{label}</div>
            {payload.map((p) => (
              <div key={p.dataKey} style={{ display: 'flex', marginTop: '5px' }}>
                <div style={{ ...customTooltipCircleStyle, backgroundColor: p.color }} />
                <div style={{ textAlign: 'right' }}>
                  {getBarName(p.dataKey?.toString(), stretchLabel, targetLabel)}:{' '}
                  {p.value?.toLocaleString()}
                </div>
              </div>
            ))}
          </Box>
        );
      }

      return null;
    },
    [stretchLabel, targetLabel]
  );

  const handleCancel = () => {
    setShowConfigDialog(false);
    setTitle(titleProps);
    setSourceType(sourceTypeProps);
    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps.split(',').map(Number));
      }
    }
  };

  return (
    <>
      <Dialog open={showConfigDialog} fullWidth>
        <DialogTitle>Configure Element</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              select
              label="Source Type"
              value={sourceType}
              onChange={sourceTypeChangeHandler}
              fullWidth
            >
              <MenuItem value="company">All</MenuItem>
              <MenuItem value="consultants">Consultant</MenuItem>
              <MenuItem value="currentuser">Current User</MenuItem>
              <MenuItem value="groups">Group</MenuItem>
            </TextField>
            {sourceType === 'consultants' && (
              <MultipleUserPicker
                value={selectedUsers}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'consultants');
                }}
                label="Consultants"
              />
            )}
            {sourceType === 'groups' && (
              <MultipleGroupPicker
                value={selectedGroups}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'groups');
                }}
                label="Groups"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onSaveConfig}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {!showConfigButton && !isLoading && errorHandler == '' && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 0,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={async () => await getRevenueDataFromServer(sourceType, userIdsProps, kioskToken)}
        >
          <RefreshIcon />
        </IconButton>
      )}
      {showConfigButton && errorHandler == '' && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 30,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={() => setShowConfigDialog(true)}
        >
          <SettingsIcon />
        </IconButton>
      )}
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" padding={1} textAlign="center" fontWeight={600} component="div">
          {title}
        </Typography>
        {isLoading && errorHandler == '' && (
          <Box m="auto">
            <CircularProgress />
          </Box>
        )}
        {errorHandler == '' && !isLoading && (
          <div style={{ height: 'calc(100% - 48px)' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart width={150} height={150} data={data ?? []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={data ? formatMonthText : undefined}
                  stroke={t.palette.text.primary}
                />
                <YAxis
                  type="number"
                  tickFormatter={(v) => v.toLocaleString()}
                  stroke={t.palette.text.primary}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" fill={t.palette.primary.main} />
                <Bar dataKey="target" fill={t.palette.secondary.main} />
                <Bar dataKey="stretch" fill={t.palette.info.main} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {!isLoading && errorHandler == '' && (
          <Typography
            variant="caption"
            padding={1}
            textAlign="left"
            component="div"
          >{`Last updated: ${cacheLastUpdated}`}</Typography>
        )}
        {errorHandler != '' && (
          <Typography
            variant="subtitle2"
            padding={1}
            textAlign="center"
            fontWeight={600}
            component="div"
          >
            {errorHandler}
          </Typography>
        )}
      </Paper>
    </>
  );
}
